import { Field } from '@/components/ui/field';
import {
  NumberInputControl,
  NumberInputField,
  NumberInputRoot,
} from '@/components/ui/number-input';
import { Switch } from '@/components/ui/switch';
import { Box, Flex, Show, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ProgramDiscountCodeFormData } from './ProgramDiscountCodeForm';

interface ShopifyOnlyDiscountFieldsProps {
  currency: string;
  defaultValues?: Partial<ProgramDiscountCodeFormData>;
}

/**
 * The discount settings only a Shopify storefront can enforce: minimum
 * purchase amount, auto-apply on tracked links, once per customer. Mounted
 * only for platform-mode brands, so its form subscriptions and effects never
 * run for brands managing their discount codes locally.
 */
export function ShopifyOnlyDiscountFields({
  currency,
  defaultValues,
}: ShopifyOnlyDiscountFieldsProps) {
  const { t } = useTranslation();
  const { control, setValue, formState } = useFormContext<ProgramDiscountCodeFormData>();

  const minPurchaseAmountValue = useWatch({ control, name: 'minPurchaseAmount' });
  const autoApplyDiscount = useWatch({ control, name: 'autoApplyDiscount' });
  const oncePerCustomer =
    useWatch({ control, name: 'oncePerCustomer' }) ?? defaultValues?.oncePerCustomer ?? true;

  const [enableMinPurchase, setEnableMinPurchase] = useState<boolean>(
    defaultValues?.minPurchaseAmount !== null && defaultValues?.minPurchaseAmount !== undefined
  );

  useEffect(() => {
    if (!enableMinPurchase) {
      setValue('minPurchaseAmount', null);
    } else if (
      enableMinPurchase &&
      (minPurchaseAmountValue === null || minPurchaseAmountValue === undefined)
    ) {
      // Set default value when enabling the switch
      setValue('minPurchaseAmount', 0);
    }
  }, [enableMinPurchase, minPurchaseAmountValue, setValue]);

  return (
    <>
      {/* Minimum Purchase Amount Field */}
      <Flex mt={4} gap={4} align="flex-start" alignContent="space-between">
        <Box flex="1">
          <Switch
            colorPalette="primary"
            checked={enableMinPurchase}
            onCheckedChange={(e) => setEnableMinPurchase(e.checked)}
          >
            {t('programs.modals.editDiscountCode.minPurchaseAmount')}
          </Switch>
          <Text fontSize="sm" color="gray.500" mt={2}>
            {t('programs.modals.editDiscountCode.minPurchaseAmountHelper')}
          </Text>
        </Box>

        <Show when={enableMinPurchase}>
          <Box>
            <Field
              invalid={!!formState.errors.minPurchaseAmount}
              errorText={formState.errors.minPurchaseAmount?.message as string}
            >
              <NumberInputRoot
                maxW="200px"
                min={0}
                max={999999.99}
                step={1}
                defaultValue={minPurchaseAmountValue?.toString() ?? '0'}
                formatOptions={{
                  style: 'currency',
                  currency: currency,
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }}
                colorPalette="primary"
                onValueChange={(details) => {
                  const value = details.valueAsNumber;
                  setValue('minPurchaseAmount', isNaN(value) ? 0 : value, {
                    shouldValidate: true,
                  });
                }}
              >
                <NumberInputField />
                <NumberInputControl />
              </NumberInputRoot>
            </Field>
          </Box>
        </Show>
      </Flex>

      {/* Auto-apply Discount on Referral Links */}
      <Box mt={4}>
        <Switch
          colorPalette="primary"
          checked={autoApplyDiscount ?? false}
          onCheckedChange={(e) => setValue('autoApplyDiscount', e.checked)}
        >
          {t('programs.modals.editDiscountCode.autoApplyDiscount')}
        </Switch>
        <Text fontSize="sm" color="gray.500" mt={2}>
          {t('programs.modals.editDiscountCode.autoApplyDiscountDescription')}
        </Text>
      </Box>

      {/* One Use Per Customer */}
      <Box mt={4}>
        <Switch
          colorPalette="primary"
          checked={oncePerCustomer}
          onCheckedChange={(e) => setValue('oncePerCustomer', e.checked)}
        >
          {t('programs.modals.editDiscountCode.oncePerCustomer')}
        </Switch>
        <Text fontSize="sm" color="gray.500" mt={2}>
          {t('programs.modals.editDiscountCode.oncePerCustomerDescription')}
        </Text>
      </Box>
    </>
  );
}
